export interface PublicTokenConfig {
  leadSourceId: number | null;
  stagePipelineId: number | null;
  stagePipelineIntentId: number | null;
  stagePipelineSignedId: number | null;
  stagePipelineQuotedId: number | null;
  campusId: number | null;
  agentId: string | null;
  signatureFormId: string | null;
  signatureFormIdEn: string | null;
  sendEmail: boolean;
  sendSms: boolean;
  requireIdentity: boolean;
  sourceDescription: string | null;
  phoneNumber?: string | null;
  whatsappNumber?: string | null;
  consentTrackingEventName: string;
  gtmBaseUrl?: string | null;
  gtmIdEn?: string | null;
  gtmIdEs?: string | null;
  googleAdsIdEn?: string | null;
  googleAdsIdEs?: string | null;
  googleAdsPhoneEn?: string | null;
  googleAdsPhoneEs?: string | null;
}

interface DecodedTokenPayload {
  context?: Record<string, unknown>;
}

const numberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const stringOrNull = (value: unknown): string | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  return String(value);
};

const boolOrDefault = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(normalized)) {
      return true;
    }
    if (["false", "0", "no", "n"].includes(normalized)) {
      return false;
    }
  }
  return fallback;
};

const decodeBase64Url = (input: string): string => {
  const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  return atob(padded);
};

export const getPublicAccessToken = (): string => {
  const lang = window.location.pathname.split("/").filter(Boolean)[0] || "es";
  const envToken = lang === "en" 
    ? process.env.REACT_APP_PUBLIC_GRAPHQL_TOKEN_EN 
    : process.env.REACT_APP_PUBLIC_GRAPHQL_TOKEN_ES;

  return new URLSearchParams(window.location.search).get("token") ||
    localStorage.getItem("public_access_token") ||
    envToken ||
    process.env.REACT_APP_PUBLIC_GRAPHQL_TOKEN ||
    "";
};

export const decodePublicTokenConfig = (): PublicTokenConfig => {
  const token = getPublicAccessToken();
  const env = process.env;
  
  // Obtener config guardada en localStorage (si existe)
  const savedConfigRaw = localStorage.getItem("public_dynamic_config");
  const savedConfig = savedConfigRaw ? JSON.parse(savedConfigRaw) : {};

  let context: Record<string, unknown> = {};

  if (token) {
    try {
      const [, payload] = token.split(".");
      if (payload) {
        const decoded = JSON.parse(
          decodeBase64Url(payload)
        ) as DecodedTokenPayload;
        context = decoded.context || {};
      }
    } catch (error) {
      console.warn("Unable to decode public token context", error);
    }
  }

  return {
    leadSourceId:
      numberOrNull(context.lead_source_id) ??
      numberOrNull(env.REACT_APP_PUBLIC_LEAD_SOURCE_ID),
    stagePipelineId:
      numberOrNull(context.stage_pipeline_id) ??
      numberOrNull(env.REACT_APP_PUBLIC_STAGE_PIPELINE_ID),
    stagePipelineIntentId:
      numberOrNull(context.stage_pipeline_intent_id) ??
      numberOrNull(env.REACT_APP_PUBLIC_STAGE_PIPELINE_ID_INTENT),
    stagePipelineSignedId:
      numberOrNull(context.stage_pipeline_signed_id) ??
      numberOrNull(env.REACT_APP_PUBLIC_STAGE_PIPELINE_ID_SIGNED),
    stagePipelineQuotedId:
      numberOrNull(context.stage_pipeline_quoted_id) ??
      numberOrNull(env.REACT_APP_PUBLIC_STAGE_PIPELINE_ID_QUOTED),
    campusId:
      numberOrNull(context.campus_id) ??
      numberOrNull(env.REACT_APP_PUBLIC_CAMPUS_ID),
    agentId:
      stringOrNull(context.agent_id) ?? stringOrNull(env.REACT_APP_PUBLIC_AGENT_ID),
    signatureFormId:
      stringOrNull(context.signature_form_es_id) ??
      stringOrNull(env.REACT_APP_PUBLIC_SIGNATURE_FORM_ES_ID),
    signatureFormIdEn:
      stringOrNull(context.signature_form_en_id) ??
      stringOrNull(env.REACT_APP_PUBLIC_SIGNATURE_FORM_EN_ID),
    sendEmail: boolOrDefault(
      context.send_email,
      boolOrDefault(env.REACT_APP_PUBLIC_SEND_EMAIL, true)
    ),
    sendSms: boolOrDefault(
      context.send_sms,
      boolOrDefault(env.REACT_APP_PUBLIC_SEND_SMS, false)
    ),
    requireIdentity: boolOrDefault(
      context.require_identity,
      boolOrDefault(env.REACT_APP_PUBLIC_REQUIRE_IDENTITY, true)
    ),
    sourceDescription:
      stringOrNull(context.source_description) ??
      stringOrNull(env.REACT_APP_PUBLIC_SOURCE_DESCRIPTION),
    phoneNumber: 
      stringOrNull(savedConfig.phoneNumber) ?? 
      stringOrNull(env.REACT_APP_PUBLIC_PHONE_NUMBER),
    whatsappNumber:
      stringOrNull(savedConfig.whatsappNumber) ??
      stringOrNull(env.REACT_APP_PUBLIC_WHATSAPP_NUMBER),
    consentTrackingEventName:
      stringOrNull(context.consent_tracking_event_name) ??
      stringOrNull(env.REACT_APP_CONSENT_TRACKING_EVENT_NAME) ??
      "ec_form_submit",
    gtmBaseUrl:
      stringOrNull(savedConfig.gtmBaseUrl) ??
      stringOrNull(env.REACT_APP_GTM_BASE_URL),
    gtmIdEn:
      stringOrNull(savedConfig.gtmIdEn) ??
      stringOrNull(env.REACT_APP_GTM_ID_EN),
    gtmIdEs:
      stringOrNull(savedConfig.gtmIdEs) ??
      stringOrNull(env.REACT_APP_GTM_ID_ES),
    googleAdsIdEn:
      stringOrNull(savedConfig.googleAdsIdEn) ??
      stringOrNull(env.REACT_APP_GOOGLE_ADS_ID_EN),
    googleAdsIdEs:
      stringOrNull(savedConfig.googleAdsIdEs) ??
      stringOrNull(env.REACT_APP_GOOGLE_ADS_ID_ES),
    googleAdsPhoneEn:
      stringOrNull(savedConfig.googleAdsPhoneEn) ??
      stringOrNull(env.REACT_APP_GOOGLE_ADS_PHONE_EN),
    googleAdsPhoneEs:
      stringOrNull(savedConfig.googleAdsPhoneEs) ??
      stringOrNull(env.REACT_APP_GOOGLE_ADS_PHONE_ES),
  };
};
